import React, { FC, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Badge } from 'transfix-ui/components/Badge'
import { GridCellValue } from 'transfix-ui/components/DataGrid'
import DataGrid, { IDataGridProps } from 'transfix-ui/components/DataGrid/DataGrid'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import { GridRowParams, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { capitalize } from 'utils/capitalize'
import { useModalState } from 'utils/hooks'
import { EditInboundFileRouterConfigButton } from './EditInboundFileRouterConfigButton'
import { useEdiInboundFileRouterConfigsLazyQuery } from './graphql/EdiInboundFileRouterConfigs'
import { InboundFileRouterConfigModal } from './modals/InboundFileRouterConfigModal'
import { TInboundFileRouterConfigModalSchema } from './schema/InboundFileRouterConfigModalSchema'

export const ITEMS_PER_PAGE = 20

interface IInboundFileRouterConfigsTableProps {
  onEdit: (options: TInboundFileRouterConfigModalSchema) => void
}

const useInboundFileRouterConfigsTableStyles = makeStyles(theme => ({
  root: {
    background: 'transparent',
    marginTop: theme.spacing(1),
    '& thead': {
      background: 'transparent',
    },
    '& th:first-child, td:first-child': {
      paddingLeft: 0,
    },
    '& td': {
      borderBottom: `1px solid ${brandColors.coolGray3}`,
    },
  },
  dataGridWrapper: {
    padding: theme.spacing(1, 2),
    margin: theme.spacing(2, 0, 2),
    width: '80vw',
  },
  dataGrid: {
    '& .MuiDataGrid-row': {
      borderBottom: 'inset',

      '&:hover': {
        cursor: 'pointer',
      },
    },
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: theme.spacing(3, 0, 0, 2),
  },
}))

const idComparator = (v1: GridCellValue, v2: GridCellValue) => {
  const num1 = Number(v1)
  const num2 = Number(v2)

  return !Number.isNaN(num1) && !Number.isNaN(num2) ? num1 - num2 : 0
}

export const InboundFileRouterConfigsTable: FC<IInboundFileRouterConfigsTableProps> = ({
  onEdit,
}) => {
  const history = useHistory()
  const classes = useInboundFileRouterConfigsTableStyles()
  const [currentValues, setCurrentValues] = useState<{}>({})
  const { createToggleHandler, modalState } = useModalState({
    dispatchEditModal: false,
  })

  const openEditHandler = createToggleHandler('dispatchEditModal', true)
  const closeEditHandler = createToggleHandler('dispatchEditModal', false)

  const [getInboundFileRouterConfigs, { data, loading }] = useEdiInboundFileRouterConfigsLazyQuery({
    fetchPolicy: 'network-only',
  })

  /* Loading the configs when page loads */
  useEffect(() => {
    getInboundFileRouterConfigs()
  }, [getInboundFileRouterConfigs])

  const { ediInboundFileRouterConfigs: items = [] } = data || {}

  const columns: IDataGridProps['columns'] = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        flex: 0.2,
        sortComparator: idComparator,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'tmsName',
        headerName: 'TMS Name',
        flex: 0.6,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'environment',
        headerName: 'Environment',
        flex: 0.5,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: 'active',
        headerName: 'Active Status',
        flex: 0.5,
        headerAlign: 'center',
        align: 'center',
        renderCell: ({ value }) => {
          const [label, tagStyle] = value
            ? ['Not Active', TAG_STYLE.error]
            : ['Active', TAG_STYLE.success]
          return (
            <Tag
              tagStyle={tagStyle}
              size={TAG_SIZE.small}
              label={label}
              style={{ width: '50%', justifyContent: 'center' }}
            />
          )
        },
      },
      {
        field: 'edit',
        headerName: 'Edit',
        flex: 0.2,
        disableClickEventBubbling: true,
        headerAlign: 'center',
        align: 'center',
        renderCell: params => (
          <EditInboundFileRouterConfigButton
            editParams={params}
            items={items}
            setValues={setCurrentValues}
            openEditHandler={openEditHandler}
          />
        ),
      },
    ],
    [items, openEditHandler]
  )

  const rows: IDataGridProps['rows'] = useMemo(
    () =>
      items?.map(inboundFileRouterConfig => ({
        id: inboundFileRouterConfig?.id,
        tmsName: capitalize(inboundFileRouterConfig?.tmsName ?? ''),
        active: inboundFileRouterConfig?.disabled,
        environment: capitalize(inboundFileRouterConfig?.environment),
        edit: null,
      })) ?? [],
    [items]
  )

  const rowClickHandler = (params: GridRowParams) => {
    history.push(`/tms-configs/file-routing-configs/${params.id}`)
  }

  return (
    <>
      <div className={classes.title}>
        <Badge
          dataTest='inbound-config-count'
          label={`${items?.length}`}
          color='gray'
          size='medium'
        />
        <Typography variant='subtitle1'>Items</Typography>
      </div>
      <div className={classes.dataGridWrapper}>
        <DataGrid
          columns={columns}
          disableColumnMenu
          headerHeight={44}
          rows={rows}
          onRowClick={params => rowClickHandler(params)}
          className={classes.dataGrid}
          autoHeight
          pagination
          sortingOrder={['asc', 'desc']}
          pageSize={ITEMS_PER_PAGE}
          loading={loading}
          componentsProps={{
            loadingOverlay: Spinner,
          }}
        />
      </div>
      {modalState.dispatchEditModal && (
        <InboundFileRouterConfigModal
          title='Edit Inbound Connection Config'
          onClose={closeEditHandler}
          defaultValues={currentValues}
          open
          onSubmit={onEdit}
        />
      )}
    </>
  )
}
